import { request } from '@/utils/http'

// ===================== 类型定义 =====================

/** 供应商评价分页查询参数 */
export interface SupplierEvaluationPageRequest {
  pageNum: number
  pageSize: number
  enterpriseId?: number
  evaluationType?: number  // 1-引进评价，2-使用评级
  ratingMonth?: string  // YYYY-MM，仅使用评级时使用
  evaluationStatus?: number  // 1-草稿，2-已提交，3-已审批
  keyword?: string
  cityCode?: string
  roleCodes?: string[]
}

/** 角色分类统计项 */
export interface RoleClassificationCount {
  roleId: number
  roleCode: string
  roleName: string
  count: number
}

/** 供应商分类数量响应 */
export interface SupplierClassificationCountResponse {
  all: number | string  // 可能是字符串或数字
  roleClassifications?: RoleClassificationCount[]  // 动态角色分类统计
  // 临时兼容旧格式（后端可能还在返回旧格式）
  introduced?: number | string
  reserved?: number | string
  notIntroduced?: number | string
}

/** 供应商评价分页返回单项 */
export interface SupplierEvaluationPageItem {
  id: number | null
  enterpriseId: number | string
  enterpriseName?: string
  cityCode?: string
  contactName?: string
  contactPhone?: string
  roleCodes?: string[]
  evaluationType: number | null
  evaluationTypeDesc?: string
  ratingMonth?: string | null
  totalScore: number | string
  evaluationResult: number | null
  evaluationResultDesc?: string
  hasVeto: boolean | null
  evaluationDate: string | null
  evaluatorName?: string | null
  evaluationStatus: number | null
  evaluationStatusDesc?: string
  dateCreated: string | null
  // 供应商管理主表相关字段
  introduceEvaluationId?: number
  introduceTotalScore?: number | string
  introduceResult?: number | null
  introduceResultDesc?: string
  usageEvaluationId?: number
  usageTotalScore?: number | string
  usageResult?: number | null
  usageResultDesc?: string
  usageRatingMonth?: string | null
}

/** 供应商评价分页返回结构 */
export interface SupplierEvaluationPageResponse {
  current: number
  size: number
  total: number
  pages: number
  records: SupplierEvaluationPageItem[]
}

/** 供应商评价详情参数 */
export interface SupplierEvaluationDetailRequest {
  id?: number
  enterpriseId?: string  // 当评价记录不存在时必传（字符串类型避免精度丢失）
  evaluationType?: number  // 当评价记录不存在时必传，1-引进评价，2-使用评级
  ratingMonth?: string  // YYYY-MM，使用评级时使用
}

/** 评分明细项 */
export interface SupplierEvaluationDetailItem {
  id: number
  evaluationId: number
  itemCode: string
  itemName: string
  category?: string
  maxScore: number
  actualScore: number
  scoreType: number
  scoreTypeDesc?: string
  eventCount: number
  evaluationDesc?: string
  relatedBusinessId?: number
  relatedBusinessType?: string
  isSystemAuto: boolean
  isExternal: boolean
  evaluatorId?: number
  evaluationRule?: string  // 评价规则/描述（从标准配置中获取）
  queryPath?: string  // 查询路径（从标准配置中获取）
}

/** 供应商评价详情返回结构 */
export interface SupplierEvaluationDetailVO {
  id: number
  enterpriseId: number
  enterpriseName?: string
  evaluationType: number
  ratingMonth?: string
  totalScore: number
  baseScore?: number
  deductionScore?: number
  bonusScore?: number
  evaluationResult: number
  evaluationResultDesc?: string
  hasVeto: boolean
  vetoReason?: string
  evaluationDate: string
  evaluatorId?: number
  evaluatorName?: string
  evaluationStatus: number
  evaluationStatusDesc?: string
  remarks?: string
  detailList: SupplierEvaluationDetailItem[]
  dateCreated: string
  dateModified: string
}

/** 供应商评价组合详情请求 */
export interface SupplierEvaluationCombinedDetailRequest {
  enterpriseId: string  // 字符串类型避免精度丢失
  ratingMonth?: string  // YYYY-MM，使用评级月份，默认查询上个月
}

/** 供应商评价组合详情返回结构 */
export interface SupplierEvaluationCombinedVO {
  enterpriseId: number
  enterpriseName?: string
  introduceEvaluation?: SupplierEvaluationDetailVO
  usageEvaluation?: SupplierEvaluationDetailVO
}

/** 创建评价记录明细项 */
export interface CreateEvaluationDetailItem {
  itemCode: string
  actualScore?: number
  eventCount?: number
  evaluationDesc?: string
  relatedBusinessId?: number
  relatedBusinessType?: string
}

/** 创建供应商评价请求 */
export interface SupplierEvaluationCreateRequest {
  enterpriseId: string  // 字符串类型避免精度丢失
  evaluationType: number  // 1-引进评价，2-使用评级
  ratingMonth?: string  // YYYY-MM，仅使用评级时必填
  detailList?: CreateEvaluationDetailItem[]
  remarks?: string
}

/** 更新评价记录明细项 */
export interface UpdateEvaluationDetailItem {
  detailId?: number
  itemCode: string
  actualScore?: number
  eventCount?: number
  evaluationDesc?: string
  relatedBusinessId?: number
  relatedBusinessType?: string
}

/** 更新供应商评价请求 */
export interface SupplierEvaluationUpdateRequest {
  id: number
  detailList?: UpdateEvaluationDetailItem[]
  remarks?: string
}

/** 提交供应商评价请求 */
export interface SupplierEvaluationSubmitRequest {
  id: number
}

/** 评价标准列表请求 */
export interface SupplierStandardListRequest {
  standardType: number  // 1-引进评价标准，2-使用评级标准
}

/** 评价标准项 */
export interface SupplierStandardItem {
  id: number
  standardType: number
  itemCode: string
  itemName: string
  category?: string
  maxScore: number
  scoreType: number
  scoreTypeDesc?: string
  evaluationRule?: string
  queryPath?: string
  remarks?: string
  isSystemAuto: boolean
  isExternal: boolean
  sortOrder: number
  status: boolean
}

// ===================== 接口实现 =====================

/**
 * 供应商评价分页查询
 */
export const fetchSupplierEvaluationPage = (params: SupplierEvaluationPageRequest) => {
  return request.post<SupplierEvaluationPageResponse>('/enterprise/tenant/supplierEvaluation/page', params)
}

/**
 * 供应商分类数量统计
 */
export const fetchSupplierClassificationCount = (params: Omit<SupplierEvaluationPageRequest, 'pageNum' | 'pageSize'>) => {
  return request.post<SupplierClassificationCountResponse>('/enterprise/tenant/supplierEvaluation/classificationCount', params)
}

/**
 * 获取供应商评价详情
 */
export const fetchSupplierEvaluationDetail = (params: SupplierEvaluationDetailRequest) => {
  return request.post<SupplierEvaluationDetailVO>('/enterprise/tenant/supplierEvaluation/detail', params)
}

/**
 * 获取供应商评价组合详情（通过企业ID查询，同时返回引进评价和使用评级）
 */
export const fetchSupplierEvaluationCombinedDetail = (params: SupplierEvaluationCombinedDetailRequest) => {
  return request.post<SupplierEvaluationCombinedVO>('/enterprise/tenant/supplierEvaluation/combinedDetail', params)
}

/**
 * 创建供应商评价记录
 */
export const createSupplierEvaluation = (params: SupplierEvaluationCreateRequest) => {
  return request.post<number>('/enterprise/tenant/supplierEvaluation/create', params)
}

/**
 * 更新供应商评价记录
 */
export const updateSupplierEvaluation = (params: SupplierEvaluationUpdateRequest) => {
  return request.post<boolean>('/enterprise/tenant/supplierEvaluation/update', params)
}

/**
 * 保存供应商评价记录（根据企业ID、评价类型和月份自动判断创建或更新）
 */
export const saveSupplierEvaluation = (params: SupplierEvaluationCreateRequest) => {
  return request.post<number>('/enterprise/tenant/supplierEvaluation/save', params)
}

/**
 * 提交供应商评价记录
 */
export const submitSupplierEvaluation = (params: SupplierEvaluationSubmitRequest) => {
  return request.post<boolean>('/enterprise/tenant/supplierEvaluation/submit', params)
}

/**
 * 获取评价标准列表
 */
export const fetchSupplierStandardList = (params: SupplierStandardListRequest) => {
  return request.post<SupplierStandardItem[]>('/enterprise/tenant/supplierEvaluation/standard/list', params)
}

/**
 * 计算评价总分
 */
export const calculateSupplierEvaluationScore = (params: { id: number }) => {
  return request.post<boolean>('/enterprise/tenant/supplierEvaluation/calculateScore', params)
}

